import { FC, useEffect, useState } from "react";
import { useTrans } from "../../../hooks";
import { VStack, Input, InputGroup, InputLeftElement } from "@chakra-ui/react";
import {
    ServiceApi,
    ServiceGroup,
    ServiceGroupApi,
    Training,
    TrainingApi,
    Typology,
    TypologyApi,
} from "../../../apis";
import { Controller, useForm } from "react-hook-form";
import { AppFilterContainer } from "../../../containers";
import {
    AppFormControl,
    AppIcon,
    AppSelect,
    AppSelectOption,
} from "../../../components";
import { useRecoilValue } from "recoil";
import { ActiveSchoolOrCompanyAtom } from "../../../atoms";

export type InternshipFilterDataType = {
    name: string;
    service: string[];
    training: string;
    typology: string[];
};

const defaultValues: InternshipFilterDataType = {
    name: "",
    service: [],
    training: "",
    typology: [],
};

export const InternshipFilter: FC<{
    filterData: any;
    setFilterData: (data: any) => void;
    children: JSX.Element;
}> = ({ filterData, setFilterData, children }) => {
    // hook
    const { t } = useTrans();
    const activeSchoolOrCompany = useRecoilValue(ActiveSchoolOrCompanyAtom);

    // state & const
    const [isInit, setIsInit] = useState(false);
    const [loadingService, setLoadingService] = useState(true);
    const [serviceGroupedOptions, setServiceGroupedOptions] = useState<
        { label: string; options: AppSelectOption[] }[]
    >([]);
    const [selectedServices, setSelectedServices] = useState<AppSelectOption[]>(
        []
    );
    const [loadingTraining, setLoadingTraining] = useState(true);
    const [trainingOptions, setTrainingOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTraining, setSelectedTraining] = useState<AppSelectOption>();
    const [loadingTypology, setLoadingTypology] = useState(true);
    const [typologyOptions, setTypologyOptions] = useState<AppSelectOption[]>(
        []
    );
    const [selectedTypologies, setSelectedTypologies] = useState<
        AppSelectOption[]
    >([]);

    const { handleSubmit, reset, control } = useForm<InternshipFilterDataType>({
        defaultValues: filterData,
        mode: "onSubmit",
    });

    // handler
    const onSubmitHandler = (formData: InternshipFilterDataType) => {
        const filterParams = {};
        Object.keys(formData).forEach((key) => {
            if (formData[key] || formData[key] === false) {
                let nKey = key;
                switch (key) {
                    case "service":
                    case "training":
                    case "typology":
                        nKey = `${key}.id`;
                        break;
                }
                if (
                    (key === "service" && formData["service"].length > 0) ||
                    (key === "typology" && formData["typology"].length > 0) ||
                    key === "training" ||
                    (key !== "service" && key !== "typology")
                ) {
                    filterParams[nKey] = formData[key];
                }
            }
        });
        setFilterData(filterParams);
    };

    const loadServiceOptions = () => {
        if (activeSchoolOrCompany?.["@type"] === "Company") {
            setLoadingService(true);
            const params = {
                [`order[name]`]: "asc",
                pagination: false,
                ["company.id"]: activeSchoolOrCompany?.id,
            };

            ServiceGroupApi.getCollectionForSelect<ServiceGroup>(1, params)
                .then(({ response }) => {
                    if (response !== null) {
                        const options = response.items.map((group) => ({
                            label: group.name,
                            options: (group.services || []).map((service) => ({
                                value: ServiceApi.toResourceIRI(service.id),
                                label: service.name,
                            })),
                        }));
                        setServiceGroupedOptions(options);
                    }
                })
                .finally(() => setLoadingService(false));
        }
    };

    const loadTrainingOptions = () => {
        setLoadingTraining(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
        };

        TrainingApi.getCollectionForSelect<Training>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((training) => ({
                        value: TrainingApi.toResourceIRI(training.id),
                        label: training.name,
                    }));
                    setTrainingOptions(options);
                }
            })
            .finally(() => setLoadingTraining(false));
    };

    const loadTypologyOptions = () => {
        setLoadingTypology(true);
        const params = {
            [`order[name]`]: "asc",
            pagination: false,
            "training.id": `${selectedTraining?.value}`,
        };

        TypologyApi.getCollectionForSelect<Typology>(1, params)
            .then(({ response }) => {
                if (response !== null) {
                    const options = response.items.map((typology) => ({
                        value: TypologyApi.toResourceIRI(typology.id),
                        label: typology.name,
                    }));
                    setTypologyOptions(options);
                }
            })
            .finally(() => setLoadingTypology(false));
    };

    const resetForm = () => {
        setFilterData({});
        setSelectedServices([]);
        setSelectedTraining(undefined);
        setSelectedTypologies([]);
        reset(defaultValues);
    };

    // useEffect
    useEffect(() => {
        if (isInit) {
            loadServiceOptions();
            loadTrainingOptions();
        }
    }, [isInit]);

    useEffect(() => {
        if (selectedTraining) {
            loadTypologyOptions();
        }
    }, [selectedTraining]);

    return (
        <AppFilterContainer
            onReset={resetForm}
            triggerElement={children}
            setIsInit={setIsInit}
            zIndex={"1400"}
        >
            <form
                id="app-filter-form"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSubmit(onSubmitHandler)();
                }}
            >
                <VStack gap={5} alignItems={"stretch"}>
                    <AppFormControl label={t("cmn:label.search")}>
                        <Controller
                            control={control}
                            name={"name"}
                            defaultValue={filterData?.name || ""}
                            render={({ field }) => (
                                <InputGroup>
                                    <InputLeftElement pointerEvents="none">
                                        <AppIcon name="ics-search" w="1rem" />
                                    </InputLeftElement>
                                    <Input {...field} />
                                </InputGroup>
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:service.label")}>
                        <Controller
                            control={control}
                            name="service"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption, true>
                                    name={field.name}
                                    isClearable
                                    isMulti={true}
                                    isLoading={loadingService}
                                    options={serviceGroupedOptions}
                                    value={selectedServices || null}
                                    placeholder={t(
                                        "ent.Internship:service.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(
                                            e?.map((option) => option?.value) ||
                                                []
                                        );
                                        setSelectedServices(
                                            e as AppSelectOption[]
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:training.label")}>
                        <Controller
                            control={control}
                            name="training"
                            defaultValue={selectedTraining?.value as string}
                            render={({ field }) => (
                                <AppSelect<AppSelectOption>
                                    name={field.name}
                                    isClearable
                                    isLoading={loadingTraining}
                                    options={trainingOptions}
                                    value={selectedTraining || null}
                                    placeholder={t(
                                        "ent.Internship:training.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(e?.value || null);
                                        setSelectedTraining(
                                            e as AppSelectOption
                                        );
                                        setSelectedTypologies([]);
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                    <AppFormControl label={t("ent.Internship:typology.label")}>
                        <Controller
                            control={control}
                            name="typology"
                            render={({ field }) => (
                                <AppSelect<AppSelectOption, true>
                                    name={field.name}
                                    isClearable
                                    isMulti={true}
                                    isLoading={loadingTypology}
                                    options={typologyOptions}
                                    value={selectedTypologies || null}
                                    placeholder={t(
                                        "ent.Internship:typology.placeholder"
                                    )}
                                    onChange={(e) => {
                                        field.onChange(
                                            e?.map((option) => option?.value) ||
                                                []
                                        );
                                        setSelectedTypologies(
                                            e as AppSelectOption[]
                                        );
                                    }}
                                />
                            )}
                        />
                    </AppFormControl>
                </VStack>
            </form>
        </AppFilterContainer>
    );
};
